import React, { useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { mixpanelEventType } from 'screens/performanceReview/components/ContinuousFeedback/Drawers/common/MeshMaven/MeshMavenRephrase';
import ReviewComment from 'screens/performanceReview/components/ReviewComment/ReviewComment';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { CheckCircle, ThumbsDownGenAi, ThumbsUpGenAi } from 'assets/assetsV1/iconComponents';

import { sendRephrasedMeshMavenContentEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';
import { CarouselControl } from 'uiToolkitV1/CarouselControl';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { RephraseOptions } from '../RephraseOptions/RephraseOptions';
import { RephraseOption } from '../RephraseOptions/types';
import MeshMavenLoading from './MeshMavenLoading';
import { useAnalyzeTextMutationLoading } from './dataHooks/analyzeText';
import { useCustomerFeedbackLoading, useCustomerFeedbackMutation } from './dataHooks/customerFeedback';
import { useRephraseTextMutation, useRephraseTextMutationLoading } from './dataHooks/rephraseText';
import {
  setRevisionNumber,
  setSelectedRevision,
  useGroupId,
  useRevisions,
  useSelectedRevision,
  useSelectedRevisionNumber,
} from './slice/meshMavenSlice';

const CustomerFeedback = () => {
  const revision = useSelectedRevision();
  const { mutate: submitCustomerFeedback } = useCustomerFeedbackMutation();

  const isFetching = useCustomerFeedbackLoading();
  const isRephraseFetching = useRephraseTextMutationLoading();

  const handleFeedback = (action: 'LIKE' | 'DISLIKE') => {
    submitCustomerFeedback({ userAction: action });
  };

  if (isRephraseFetching || !revision.requestId) {
    return null;
  }

  if (revision.isAIFeedbackProvided) {
    return (
      <Flex alignItems="center" p={4} m="-1" mt="auto">
        <CheckCircle color="#44812F" />
        <TextBodyMedium color="#44812F" ml="4px">
          Thank you! Your response will help us improve Maven
        </TextBodyMedium>
      </Flex>
    );
  }

  return (
    <Flex
      borderTopWidth="1px"
      borderColor="neutral.EBEFF3"
      m="-1"
      p={4}
      mt="auto"
      alignItems="center"
      bg="linear-gradient(91deg, rgba(0, 142, 240, 0.03) 1.81%, rgba(236, 59, 100, 0.03) 36.92%, rgba(255, 194, 0, 0.03) 69.52%, rgba(92, 196, 97, 0.03) 106.15%)"
    >
      <TextBodyMedium>Did you like my response?</TextBodyMedium>
      <Button
        isDisabled={isFetching}
        onClick={() => handleFeedback('LIKE')}
        StartIcon={ThumbsUpGenAi}
        variant="tertiary"
        ml="auto"
      />
      <Button
        isDisabled={isFetching}
        onClick={() => handleFeedback('DISLIKE')}
        StartIcon={ThumbsDownGenAi}
        variant="tertiary"
        ml={4}
      />
    </Flex>
  );
};

const RephraseText = ({ reviewCommentPlaceholder, reviewId, revieweeId, questionId }) => {
  const { mutate: rephraseText, isLoading: isRephraseLoading } = useRephraseTextMutation();
  const isAnalyzeLoading = useAnalyzeTextMutationLoading();
  const groupId = useGroupId();
  const revisions = useRevisions();
  const revision = useSelectedRevision();
  const revisionNumber = useSelectedRevisionNumber();
  const dispatch = useDispatch();
  const [rephraseLoadingText, setRephraseLoadingText] = useState('');

  const { employeeId, providerId } = useReviewFormParams();
  const queryClient = useQueryClient();
  const sidebarData = queryClient.getQueryData([REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId]);
  const { reviewInfo } = sidebarData ?? {};
  const { providerType } = reviewInfo ?? {};

  if (!revision) {
    return null;
  }

  const handleRephraseText = (rephraseOption: RephraseOption, loadingText: string) => {
    setRephraseLoadingText(loadingText.replace('moduleName', 'review'));

    sendRephrasedMeshMavenContentEvent({
      eventProperties: {
        source: providerType,
        type: mixpanelEventType[rephraseOption],
      },
    });

    rephraseText({
      reviewId,
      empId: revieweeId,
      questionId,
      feedbackText: revision.updatedState,
      groupId,
      revisionNumber,
      rephraseInstruction: rephraseOption,
    });
  };

  const handleBlur = (newTextHtml: string, newText: string) => {
    dispatch(setSelectedRevision({ revision: { ...revision, updatedState: newText, updatedStateHtml: newTextHtml } }));
  };

  const handleChange = (newTextHtml: string, newText: string) => {
    dispatch(
      setSelectedRevision({
        revision: { ...revision, isReanalyzeEnabled: true, isTextLengthValid: newText.length >= 22 },
      })
    );
  };

  const handleRevisionNumberChange = (index: number) => {
    dispatch(setRevisionNumber({ revisionNumber: index }));
  };

  return (
    <Flex
      w={{ base: '100%', desktop: '55%' }}
      flexDir="column"
      borderWidth="1px"
      borderColor="neutral.EBEFF3"
      borderRadius="8px"
      p={1}
    >
      <Flex
        justify="space-between"
        alignItems="center"
        p="6px 12px"
        borderBottomWidth="1px"
        borderBottomColor="neutral.EBEFF3"
      >
        <TextBodyMedium bold>Comment</TextBodyMedium>
        <RephraseOptions
          handleRephraseText={handleRephraseText}
          tooltipText={
            revision.isTextLengthValid ? '' : 'Add more text to leverage Maven. Currently supports English only.'
          }
          isDisabled={!revision.isTextLengthValid || isRephraseLoading || isAnalyzeLoading}
          totalCharacters={revision.updatedState.length}
        />
      </Flex>
      {isRephraseLoading ? (
        <Box my="auto">
          <MeshMavenLoading loadingText={rephraseLoadingText} />
        </Box>
      ) : (
        <>
          {/* @ts-ignore */}
          <ReviewComment
            label={null}
            initialTextHTML={revision.updatedStateHtml}
            initialText={revision.updatedState}
            placeholder={reviewCommentPlaceholder}
            key={`${revisionNumber}-${revision.updatedState.length}`}
            className="editor-container-v1"
            // @ts-ignore
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {revisions.length > 1 && (
            <CarouselControl
              selectedIndex={revisionNumber}
              onChange={handleRevisionNumberChange}
              noOfTabs={revisions.length}
              isDisabled={isAnalyzeLoading}
            />
          )}
          <CustomerFeedback />
        </>
      )}
    </Flex>
  );
};

export default RephraseText;
